import { AlertTriangle, RefreshCw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { getRuntimeConfig, isRuntimeConfigReady } from "@/config/runtime";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = () => {
    globalThis.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div data-testid="error-boundary" className="flex h-screen items-center justify-center bg-surface-base">
          <div className="text-center max-w-md px-6">
            <div className="h-16 w-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
            <h1 data-testid="error-boundary-title" className="text-2xl font-semibold text-content-primary">
              Something went wrong
            </h1>
            <p data-testid="error-boundary-message" className="mt-3 text-content-tertiary">
              An unexpected error occurred. You can try reloading the page or going back.
            </p>
            {isRuntimeConfigReady() && getRuntimeConfig().isDev && this.state.error && (
              <pre
                data-testid="error-boundary-details"
                className="mt-4 p-3 bg-surface-primary border border-border-primary rounded-lg text-left text-xs text-red-400 overflow-auto max-h-32"
              >
                {this.state.error.message}
              </pre>
            )}
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                data-testid="error-boundary-try-again"
                onClick={this.handleReset}
                className="px-4 py-2.5 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm font-medium text-content-primary transition-colors"
              >
                Try Again
              </button>
              <button
                data-testid="error-boundary-reload"
                onClick={this.handleReload}
                className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <RefreshCw className="h-4 w-4" />
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
